import { ProjectForm, type LeadDefaults } from '@/components/features/Projects/forms/ProjectForm';
import type { PersonOption } from '@/components/global/PersonSelect';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { useTranslations } from '@/hooks/useTranslations';

interface CreateProjectFromLeadSheetProps {
    /** The lead's values, as the project form wants them. */
    defaults: LeadDefaults;
    /** Everyone who can own a project, commercially-responsible roles first. */
    ownerCandidates?: { people: PersonOption[]; suggested_ids: string[] };
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * Converting a lead, in a sheet over the lead it came from.
 *
 * The body is the same ProjectForm the create page renders, so the two never
 * drift — only the container differs. Submitting navigates to the new project,
 * which unmounts the sheet with it.
 */
export default function CreateProjectFromLeadSheet({ defaults, ownerCandidates, open, onOpenChange }: CreateProjectFromLeadSheetProps) {
    const { t } = useTranslations();

    return (
        <ResponsiveModal open={open} onOpenChange={onOpenChange} variant="sheet">
            <ResponsiveModalContent size="2xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('projects.create.from_lead.title', 'Create Project from Lead')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('projects.create.subheading_from_lead', 'Converting lead :lead — the lead will be marked as won.').replace(
                            ':lead',
                            defaults.lead_number ?? '',
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody className="px-4 py-6 md:px-6">
                    <ProjectForm ownerCandidates={ownerCandidates} initialLead={defaults} onCancel={() => onOpenChange(false)} />
                </ResponsiveModalBody>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
